import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import toastr from 'helpers/toastr';
import { Modal, Button } from 'antd';
import ViewPanel from 'vcomponents/Form/ViewPanel';
import { DISPLAY_STATUS_DESCRIPTION } from './viewMeta';

import {
  IQuickAddress,
  IQuickOrderDetail,
  TDisplayStatus,
  QUICK_DISPLAY_STATUS,
  QUICK_DISPLAY_STATUS_STYLE,
  QUICK_TYPE,
  QUICK_VEHICLE,
  SERVICE_TYPE_OPTION
} from '../types';
import { useGetQuickOrderDetail, useCancelQuickOrder, useGetPickerInfo } from '../hooks';

import markerIcon from './marker.png';
import RefreshIcon from './ic_refresh_black.svg?react';
import {
  ArrowDivider,
  Card,
  CardHeader,
  CardTitle,
  Columns,
  DetailGrid,
  Divider,
  FareRow,
  Field,
  FieldLabel,
  FieldValue,
  InfoBanner,
  MapBox,
  MapMeta,
  MapPlaceholder,
  Mask,
  MetaItem,
  MetaList,
  OrdererName,
  PageWrap,
  PartyLabel,
  PartyName,
  PartyText,
  RefetchButton,
  RightColumn,
  StatusBadge,
  Step,
  StepDot,
  StepLabel,
  Stepper,
  StepTime,
  StatusSummary,
  SummaryRow,
  TotalFare,
  TotalFareLabel,
  TotalRow,
  WarnBox,
  WarnItem
} from './style';

const GREEN = '#30B843';
const RED = '#FA5252';
const BLUE = '#0066FF';

const won = (value?: number) => `₩${(value ?? 0).toLocaleString()}`;

const QUICK_FLOW: TDisplayStatus[] = ['ORDER_RECEIVED', 'DRIVER_ASSIGNED', 'PICKUP_COMPLETE', 'DELIVERY_COMPLETE'];
const CANCELLED_FLOW: TDisplayStatus[] = ['ORDER_RECEIVED', 'ORDER_CANCELLED'];

interface QuickReservationDetailProps {
  location: any;
  history: any;
  match: any;
}

function QuickReservationDetail({ location, history, match }: QuickReservationDetailProps) {
  const [quickDetailData, setQuickDetailData] = useState<IQuickOrderDetail>();
  const [dataUpdatedAt, setDataUpdatedAt] = useState<string>();
  const [naverMap, setNaverMap] = useState<naver.maps.Map>();
  const [naverMarker, setNaverMarker] = useState<naver.maps.Marker>();
  const [isViewPanelLoading, setIsViewPanelLoading] = useState<boolean>(false);

  const { mutateAsync: getQuickOrderDetail, isLoading } = useGetQuickOrderDetail();
  const { mutateAsync: cancelQuickOrder } = useCancelQuickOrder();

  const enabled =
    quickDetailData?.displayStatus === 'PICKUP_COMPLETE' || quickDetailData?.displayStatus === 'DRIVER_ASSIGNED';
  const { data: pickerInfo, refetch: getPickerInfo } = useGetPickerInfo(
    quickDetailData?.quickManagerIdx,
    enabled && !isLoading
  );

  const getOrderDetail = async () => {
    const routeQuickIdx = Number(match?.params?.quickManagerIdx);
    const stateQuickIdx = Number(location?.state?.quickIdx);
    const quickIdx = routeQuickIdx || stateQuickIdx;

    if (quickIdx) {
      const quickData = await getQuickOrderDetail(quickIdx);
      setIsViewPanelLoading(false);
      const formatQuickData = {
        ...quickData,
        orderType: QUICK_TYPE[quickData?.orderType] || '-',
        serviceType: SERVICE_TYPE_OPTION[quickData?.serviceType] || '-',
        vehicle: QUICK_VEHICLE[quickData?.vehicle],
        appointmentDate: moment(quickData?.appointmentDate).format('YYYY-MM-DD HH:mm'),
        createDate: moment(quickData?.createDate).format('YYYY-MM-DD HH:mm'),
        goodsInfo: quickData?.goodsInfo || '-',
        remark: quickData?.remark || '-'
      };
      setQuickDetailData(formatQuickData);
      return formatQuickData;
    }
    setIsViewPanelLoading(false);
    return null;
  };

  const initNaverMaps = async () => {
    const { naver } = window;
    const isMap = document.getElementById('map');

    if (naver?.maps && isMap) {
      setDataUpdatedAt(moment().format('YYYY-MM-DD HH:mm:ss'));
      if (!naverMarker || quickDetailData?.displayStatus === 'ORDER_RECEIVED') {
        const latitude = pickerInfo?.latitude || '37.515022';
        const longitude = pickerInfo?.longitude || '127.0614107';
        const position = new naver.maps.LatLng(latitude, longitude);

        const mapOptions = { center: position, zoom: 17 };
        const map = new naver.maps.Map('map', mapOptions);

        const anchor = new naver.maps.Point(18, 48);
        const markerContent = `<img src=${markerIcon} alt="" style="width: 36px; height: 48px;" />`;
        const markerOptions = { position, map, icon: { anchor, content: markerContent } };
        const marker = new naver.maps.Marker(markerOptions);

        setNaverMap(map);
        setNaverMarker(marker);
      } else {
        // eslint-disable-next-line no-unused-expressions
        naverMarker?.setPosition({ lat: pickerInfo?.latitude, lng: pickerInfo?.longitude });
        // eslint-disable-next-line no-unused-expressions
        naverMap?.setCenter({ lat: pickerInfo?.latitude, lng: pickerInfo?.longitude });
        // eslint-disable-next-line no-unused-expressions
        naverMap?.setZoom(17);
      }
    }
  };

  const handleRefetch = async () => {
    const quickData = await getOrderDetail();
    if (quickData && quickData?.displayStatus !== 'ORDER_RECEIVED') {
      getPickerInfo();
    } else {
      toastr('top-right', 'warning', '배송기사 배차완료 후 위치 검색이 가능합니다.');
    }
  };

  useEffect(() => {
    if ((pickerInfo && enabled) || quickDetailData?.displayStatus === 'ORDER_RECEIVED') {
      initNaverMaps();
    }
  }, [quickDetailData?.displayStatus, pickerInfo]);

  useEffect(() => {
    setIsViewPanelLoading(true);
    getOrderDetail();
  }, []);

  const handleGoBack = () => {
    history.push('/service/quick/reservation/list');
  };

  const cancelSuccess = () => {
    Modal.success({ title: '퀵대장 취소를 완료했습니다.', icon: null, content: null });
  };

  const handleCancelOrder = () => {
    Modal.confirm({
      title: '퀵대장 배차를 취소 하시겠습니까?',
      icon: null,
      content: null,
      okText: '예약 취소',
      onOk: async () => {
        if (quickDetailData?.quickManagerIdx) {
          await cancelQuickOrder(quickDetailData?.quickManagerIdx);
          cancelSuccess();
          handleGoBack();
        }
      }
    });
  };

  const handleReapply = () => {
    Modal.confirm({
      title: '상세내용과 동일하게 재요청을 진행하시겠습니까?',
      icon: null,
      content: null,
      okText: '네',
      onOk: async () => {
        history.push({ pathname: '/service/quick/reservation/booking', data: quickDetailData });
      }
    });
  };

  const displayStatus = quickDetailData?.displayStatus;
  const statusColor = (displayStatus && QUICK_DISPLAY_STATUS_STYLE[displayStatus]) || GREEN;

  const quickCancelButton =
    displayStatus === 'ORDER_RECEIVED' ? (
      <Button key="cancel" onClick={handleCancelOrder}>
        배차 취소
      </Button>
    ) : (
      <span key="status" style={{ color: '#868e96' }}>
        상태 : {QUICK_DISPLAY_STATUS[displayStatus]}
      </span>
    );

  const quickReapplyButton = (
    <Button key="reapply" type="primary" style={{ background: GREEN, borderColor: GREEN }} onClick={handleReapply}>
      퀵 재요청하기
    </Button>
  );
  const buttons = [
    quickCancelButton,
    quickReapplyButton,
    <Button key="back" onClick={handleGoBack}>
      뒤로가기
    </Button>
  ];

  const getAddress = (type: IQuickAddress['addressType']) =>
    quickDetailData?.quickAddressList?.find(({ addressType }) => addressType === type);

  const renderParty = (type: IQuickAddress['addressType'], labelText: string, dotColor: string) => {
    const address = getAddress(type);
    if (!address) return null;
    const isLarge = quickDetailData?.quickGoodsType === 'L';
    const fullAddress = [address.roadAddress, address.addressDetail].filter(Boolean).join(' ');
    return (
      <div>
        <PartyLabel color={dotColor}>{labelText}</PartyLabel>
        <PartyName>{address.name}</PartyName>
        {fullAddress ? <PartyText>{fullAddress}</PartyText> : null}
        {address.cellphone ? <PartyText>{address.cellphone}</PartyText> : null}
        {isLarge && type !== 'WAYPOINT' ? (
          <>
            <PartyText>배송 운반 지원 : {address.loadingMethodDescription || '-'}</PartyText>
            <PartyText>
              {type === 'STARTPOINT' ? '상차' : '하차'} 시 기사님 확인사항 : {address.description || '-'}
            </PartyText>
          </>
        ) : null}
      </div>
    );
  };

  const hasWaypoint = !!getAddress('WAYPOINT');

  const steps = useMemo(() => {
    const log: Record<string, string> = {};
    (quickDetailData?.displayStatusLog || []).forEach(({ status, updatedAt }) => {
      log[status] = updatedAt;
    });
    const isCancelled = displayStatus === 'ORDER_CANCELLED' || !!log.ORDER_CANCELLED;
    const flow = isCancelled ? CANCELLED_FLOW : QUICK_FLOW;
    return flow.map((status, index) => {
      const done = !!log[status];
      return {
        status,
        label: QUICK_DISPLAY_STATUS[status],
        time: log[status] ? moment(log[status]).format('MM-DD HH:mm') : '',
        done,
        isFirst: index === 0,
        color: GREEN
      };
    });
  }, [quickDetailData?.displayStatusLog, displayStatus]);

  const latestLog = useMemo(() => {
    const log = quickDetailData?.displayStatusLog || [];
    return log[log.length - 1];
  }, [quickDetailData?.displayStatusLog]);

  const summaryStatus = (latestLog?.status || displayStatus) as TDisplayStatus | undefined;
  const summaryColor = (summaryStatus && QUICK_DISPLAY_STATUS_STYLE[summaryStatus]) || GREEN;
  const summaryDesc = (summaryStatus && DISPLAY_STATUS_DESCRIPTION[summaryStatus]) || [];

  return (
    <ViewPanel loading={isViewPanelLoading} theme="none" title="퀵 예약 상세" buttons={buttons}>
      <PageWrap>
        <InfoBanner>
          퀵 예약 후 발생하는 모든 문의사항은 카카오T 고객센터(1599-9400) 로 문의해 주시길 바랍니다.
        </InfoBanner>

        {/* 예약 기본 정보 */}
        <Card>
          <CardTitle>예약 기본 정보</CardTitle>
          <SummaryRow>
            <div>
              <StatusBadge color={statusColor}>{QUICK_DISPLAY_STATUS[displayStatus]}</StatusBadge>
              <OrdererName>주문자 : {quickDetailData?.userName}</OrdererName>
              <MetaList>
                <MetaItem>예약 번호 : {quickDetailData?.vendorOrderId}</MetaItem>
                <MetaItem>예약 일시 : {quickDetailData?.createDate}</MetaItem>
              </MetaList>
            </div>
            <div>
              <TotalFare>{won(quickDetailData?.totalFee)}</TotalFare>
              <TotalFareLabel>총 운임</TotalFareLabel>
            </div>
          </SummaryRow>
        </Card>

        <Columns>
          {/* 발송 정보 + 운임 */}
          <Card>
            <CardTitle>발송 정보</CardTitle>

            {renderParty('STARTPOINT', '보내는 분', BLUE)}
            <ArrowDivider>↓</ArrowDivider>
            {hasWaypoint ? (
              <>
                {renderParty('WAYPOINT', '경유지', '#ffa94d')}
                <ArrowDivider>↓</ArrowDivider>
              </>
            ) : null}
            {renderParty('ENDPOINT', '받는 분', RED)}

            <Divider />

            <DetailGrid>
              <Field>
                <FieldLabel>픽업 시간</FieldLabel>
                <FieldValue>{quickDetailData?.appointmentDate}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>차량</FieldLabel>
                <FieldValue>{quickDetailData?.vehicle}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>퀵 상품</FieldLabel>
                <FieldValue>{quickDetailData?.orderType}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>물품 정보</FieldLabel>
                <FieldValue>{quickDetailData?.goodsInfo}</FieldValue>
              </Field>
              <Field className="full">
                <FieldLabel>배송 메모</FieldLabel>
                <FieldValue>{quickDetailData?.remark}</FieldValue>
              </Field>
            </DetailGrid>

            <Divider />

            <FareRow>
              <span>운임가</span>
              <span>{won(quickDetailData?.companyPrice)}</span>
            </FareRow>
            <FareRow muted>
              <span>할인/이용금액</span>
              <span>{won(quickDetailData?.salesPrice)}</span>
            </FareRow>
            <FareRow muted>
              <span>취소 수수료</span>
              <span>{won(quickDetailData?.cancelFee)}</span>
            </FareRow>
            <TotalRow>
              <span>합계</span>
              <span>{won(quickDetailData?.totalFee)}</span>
            </TotalRow>
          </Card>

          <RightColumn>
            {/* 배송 상태 */}
            <Card>
              <CardTitle>배송 상태</CardTitle>
              <Stepper>
                {steps.map((step) => (
                  <Step key={step.status} done={step.done} color={step.color} isFirst={step.isFirst}>
                    <StepDot done={step.done} color={step.color} />
                    <StepLabel done={step.done}>{step.label}</StepLabel>
                    {step.time ? <StepTime>{step.time}</StepTime> : null}
                  </Step>
                ))}
              </Stepper>
              {latestLog ? (
                <StatusSummary color={summaryColor}>
                  <div className="summary-title">
                    {QUICK_DISPLAY_STATUS[latestLog.status]} ·{' '}
                    {moment(latestLog.updatedAt).format('YYYY-MM-DD HH:mm:ss')}
                  </div>
                  <ul>
                    {summaryDesc.map((content) => (
                      <li key={content}>{content}</li>
                    ))}
                  </ul>
                </StatusSummary>
              ) : null}
            </Card>

            {/* 기사 위치 */}
            <Card>
              <CardHeader>
                <CardTitle>기사 위치</CardTitle>
                <RefetchButton onClick={handleRefetch}>
                  <RefreshIcon />
                  <span>위치 재검색</span>
                </RefetchButton>
              </CardHeader>
              <MapBox>
                {displayStatus === 'ORDER_RECEIVED' ? <Mask>배차 완료 후 확인이 가능합니다.</Mask> : null}
                <MapPlaceholder>지도 영역 (네이버 지도)</MapPlaceholder>
                <div id="map" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
              </MapBox>
              <MapMeta>배송기사 위치 최종 조회: {dataUpdatedAt || '-'}</MapMeta>
              <WarnBox>
                <WarnItem>배송 당시, 기상 악화가 있을 경우, 운임이 조정될 수 있습니다.</WarnItem>
                <WarnItem>배송 과정에서 다양한 사유로 변차 발생시, 확정운임이 변경될 수 있습니다.</WarnItem>
                <WarnItem>
                  확정 운임의 변경과 관련된 문의는 카카오T 고객센터(1599-9400) 로 문의해 주시길 바랍니다.
                </WarnItem>
              </WarnBox>
            </Card>
          </RightColumn>
        </Columns>
      </PageWrap>
    </ViewPanel>
  );
}

export default QuickReservationDetail;
